/* eslint complexity: ["error", 8] -- This file is complex */
import {
  AnalyticsFieldName,
  LegalLinksAnalyticsEvent,
  OnboardingAnalyticsPageView,
} from "@/constants/analytics/Analytics";
import { ConsentText } from "@/constants/Onboarding";
import ProcessEnvironment from "@/constants/ProcessEnvironment";
import { OnboardingConsentTestID } from "@/constants/TestID";
import { UserConsentType } from "@/constants/User";
import { useAnalytics } from "@/services/Analytics";
import { useAppUserSelector } from "@/store/reducers/appUser";
import ConsentCard from "@/ui/components/onboarding/consent/ConsentCard";
import { Text, TextLink } from "@/ui/core";
import { HStack, VStack } from "@/ui/layout";
import { joinTestIDs } from "@/utils/string.util";

const baseTestID = OnboardingConsentTestID.Primary;

interface ConsentSectionProps {
  isRecordingConsentAlreadyExist: boolean;
  isTermsAndPrivacyAccepted: boolean;
  isTermsAndPrivacyAcceptedManually: boolean;
  onPatientConsentChange: ({
    isChecked,
    patientId,
  }: PatientConsentChangeProps) => Promise<void>;
  onTermsAndPrivacyChange: (isChecked: boolean) => Promise<void>;
  patientConsentStates: Record<string, boolean>;
  patientsNeedingConsent?: LinkedPatientProfile[];
  shouldShowConsentToRecording: boolean;
}

const ConsentSection = ({
  isRecordingConsentAlreadyExist,
  isTermsAndPrivacyAccepted,
  isTermsAndPrivacyAcceptedManually,
  onPatientConsentChange,
  onTermsAndPrivacyChange,
  patientConsentStates,
  patientsNeedingConsent: initialPatientsNeedingConsent,
  shouldShowConsentToRecording,
}: ConsentSectionProps) => {
  const { track } = useAnalytics();
  const patientsNeedingConsent = initialPatientsNeedingConsent ?? [];
  const user = useAppUserSelector();
  const { isPatient: isAppUserPatient } = user;

  const trackLegalLinkPressed = (eventName: string) => {
    track(eventName, {
      [AnalyticsFieldName.Component]: OnboardingAnalyticsPageView.Consent,
      [AnalyticsFieldName.Source]: OnboardingAnalyticsPageView.Consent,
    });
  };

  const termsAndPrivacyTitle = (
    <HStack
      alignItems="center"
      flexWrap="wrap"
      gap={2}
      testID={joinTestIDs(baseTestID, "terms-privacy-title")}
    >
      <Text
        testID={joinTestIDs(baseTestID, "title-prefix")}
        variant="bodyMedium"
      >
        I agree to Equip&apos;s{" "}
      </Text>
      <TextLink
        isUnderlined
        onPress={() =>
          trackLegalLinkPressed(LegalLinksAnalyticsEvent.TermsOfUse)
        }
        testID={joinTestIDs(baseTestID, "terms-link")}
        text="Terms of Use"
        url={ProcessEnvironment.EquipTermsOfUse}
      />
      <Text testID={joinTestIDs(baseTestID, "suffix")} variant="bodyMedium">
        {" "}
        and
      </Text>
      <TextLink
        isUnderlined
        onPress={() =>
          trackLegalLinkPressed(LegalLinksAnalyticsEvent.PrivacyPolicy)
        }
        testID={joinTestIDs(baseTestID, "privacy-link")}
        text="Privacy Policy"
        url={ProcessEnvironment.EquipPrivacyPolicy}
      />
    </HStack>
  );

  const shouldRenderTermsAndPrivacy =
    isTermsAndPrivacyAcceptedManually || !isTermsAndPrivacyAccepted;
  const shouldRenderConsentToRecording = !isRecordingConsentAlreadyExist;

  return (
    <VStack gap={18}>
      {shouldRenderTermsAndPrivacy && (
        <ConsentCard
          consentType={UserConsentType.TermsAndPrivacy}
          isChecked={isTermsAndPrivacyAccepted}
          onChange={onTermsAndPrivacyChange}
          testID={joinTestIDs(baseTestID, "terms-privacy")}
          title={termsAndPrivacyTitle}
        />
      )}
      {shouldRenderConsentToRecording && shouldShowConsentToRecording && (
        <ConsentCard
          consentType={UserConsentType.Recording}
          isAppUserPatient={isAppUserPatient}
          onPatientConsentChange={onPatientConsentChange}
          patientConsentStates={patientConsentStates}
          patientsNeedingConsent={
            isAppUserPatient ? [user] : patientsNeedingConsent
          }
          testID={joinTestIDs(baseTestID, "session-recording")}
        >
          <Text
            testID={joinTestIDs(baseTestID, "session-recording-title")}
            variant="captionMedium"
          >
            Your privacy is important to us
          </Text>
          <Text
            testID={joinTestIDs(baseTestID, "session-recording-description")}
            variant="caption"
          >
            {ConsentText.RecordingConsent}
          </Text>
        </ConsentCard>
      )}
    </VStack>
  );
};

export type { ConsentSectionProps };
export default ConsentSection;
